<template>
  <div class="sales-report-page py-3">
    <header class="sr-toolbar">
      <div class="sr-toolbar__title">
        <span class="sr-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Sales Report</h1>
          <p>User-wise orders, bottles, and return rate for a date range</p>
        </div>
      </div>
      <div class="sr-toolbar__actions">
        <button
          type="button"
          class="sr-export-btn"
          :class="{ 'is-loading': isExporting }"
          :disabled="isExporting || !rows.length"
          @click="exportReport"
        >
          <svg v-if="!isExporting" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{{ isExporting ? 'Exporting…' : 'Export CSV' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="sr-alert">{{ errorMessage }}</CAlert>

    <section class="sr-section">
      <header class="sr-section__head">
        <h2>Filters</h2>
        <small class="sr-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="sr-section__body">
        <div class="sr-filters">
          <div class="sr-field sr-field--range">
            <label class="sr-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="sr-field sr-field--presets">
            <label class="sr-label" aria-hidden="true">&nbsp;</label>
            <div class="sr-presets" role="group" aria-label="Quick date ranges">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="sr-preset"
                :class="{ 'is-active': activePreset === preset.key }"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>
          <div class="sr-field sr-field--action">
            <label class="sr-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="sr-search-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="searchReport">
              <svg v-if="!isLoading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>{{ isLoading ? 'Searching…' : 'Search' }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Sales by User"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      empty-text="No sales data for the selected range."
    >
      <template #toolbar>
        <span v-if="rows.length" class="sr-totals">
          <span class="sr-totals__chip">Orders <strong>{{ totalOrders }}</strong></span>
          <span class="sr-totals__chip">Bottles <strong>{{ totalBottles }}</strong></span>
          <span v-if="isManakkalAgroOrganisation" class="sr-totals__chip">Kg <strong>{{ totalKg }}</strong></span>
          <span v-if="isManakkalVetOrganisation" class="sr-totals__chip">Farm Bottles <strong>{{ totalFarmBottles }}</strong></span>
          <span class="sr-totals__chip">Returned <strong>{{ totalReturnedBottles }}</strong></span>
        </span>
      </template>

      <template #cell-return_rate="{ row }">
        <span :class="['sr-rate', returnRateClass(row.return_rate_value)]">{{ row.return_rate_display }}</span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

// Same org-split as the admin Dashboard's Sales By Person table (see
// Dashboard.vue isManakkalAgroOrganisation/isManakkalVetOrganisation):
// Manakkal Agro (org 1) reports Agro Yield Super 1kg separately as "Kg";
// Manakkal Vet (org 4) reports "farm" products separately as "Farm
// Bottles". Neither counts toward "Total Bottles" — see server/CLAUDE.md
// "Reports must reconcile with the Dashboard".
const MANAKKAL_AGRO_ORGANISATION_ID = 1
const MANAKKAL_VET_ORGANISATION_ID = 4

export default {
  name: 'SalesReport',
  components: { DateRangePicker, BaseTable },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    return {
      fromDate: today,
      toDate: today,
      rows: [],
      errorMessage: '',
      isLoading: false,
      isExporting: false,
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: 'mtd', label: 'This Month' },
        { key: 'last-month', label: 'Last Month' }
      ]
    }
  },
  computed: {
    isManakkalAgroOrganisation () {
      return Number(this.$store.state.activeOrganisationId) === MANAKKAL_AGRO_ORGANISATION_ID
    },
    isManakkalVetOrganisation () {
      return Number(this.$store.state.activeOrganisationId) === MANAKKAL_VET_ORGANISATION_ID
    },
    // Kg (org 1) / Farm Bottles (org 4) are inserted right after Total
    // Bottles, matching the field order the backend returns them in
    // (salesreport()/downloadsalesreport() — total_kg / total_farm_bottles).
    // Only one is ever shown, since a user only ever belongs to one org
    // at a time; org 2 (MDI/HMS) shows neither, matching the backend's
    // "no exclusion" case.
    columns () {
      const cols = [
        { key: 'user', label: 'User' },
        { key: 'total_orders', label: 'Total Orders', align: 'right', format: 'number' },
        { key: 'total_bottles', label: 'Total Bottles', align: 'right', format: 'number' }
      ]
      if (this.isManakkalAgroOrganisation) {
        cols.push({ key: 'total_kg', label: 'Kg', align: 'right', format: 'number' })
      }
      if (this.isManakkalVetOrganisation) {
        cols.push({ key: 'total_farm_bottles', label: 'Farm Bottles', align: 'right', format: 'number' })
      }
      cols.push(
        { key: 'returned_orders', label: 'Returned Orders', align: 'right', format: 'number' },
        { key: 'returned_bottles', label: 'Returned Bottles', align: 'right', format: 'number' },
        { key: 'return_rate', label: 'Return Rate', align: 'right' },
        { key: 'elligible_bottles', label: 'Eligible Bottles', align: 'right', format: 'number' }
      )
      return cols
    },
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    },
    tableRows () {
      return this.rows.map((row) => ({
        ...row,
        return_rate_value: this.parseRate(row.return_rate),
        return_rate_display: this.formatRate(row.return_rate)
      }))
    },
    totalOrders () {
      return this.rows.reduce((sum, r) => sum + (Number(r.total_orders) || 0), 0)
    },
    totalBottles () {
      return this.rows.reduce((sum, r) => sum + (Number(r.total_bottles) || 0), 0)
    },
    totalKg () {
      return this.rows.reduce((sum, r) => sum + (Number(r.total_kg) || 0), 0)
    },
    totalFarmBottles () {
      return this.rows.reduce((sum, r) => sum + (Number(r.total_farm_bottles) || 0), 0)
    },
    totalReturnedBottles () {
      return this.rows.reduce((sum, r) => sum + (Number(r.returned_bottles) || 0), 0)
    }
  },
  created () {
    this.searchReport()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    toIso (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case 'mtd':
          start.setDate(1)
          break
        case 'last-month':
          start.setMonth(today.getMonth() - 1, 1)
          end.setMonth(today.getMonth(), 0)
          break
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    parseRate (raw) {
      if (raw == null) return null
      const n = parseFloat(String(raw).replace(/[^0-9.+-]/g, ''))
      return Number.isFinite(n) ? n : null
    },
    formatRate (raw) {
      const n = this.parseRate(raw)
      if (n == null) return raw == null ? '—' : String(raw)
      const str = String(raw)
      if (str.includes('%')) return `${n.toFixed(2)}%`
      return n <= 1 ? `${(n * 100).toFixed(2)}%` : `${n.toFixed(2)}%`
    },
    returnRateClass (value) {
      if (value == null) return 'sr-rate--muted'
      const normalised = value > 1 ? value : value * 100
      if (normalised >= 25) return 'sr-rate--high'
      if (normalised >= 10) return 'sr-rate--mid'
      return 'sr-rate--low'
    },
    searchReport () {
      this.errorMessage = ''
      this.isLoading = true
      axios.post(
        `${this.$apiAdress}/api/sales_report?token=${this.authToken()}`,
        { fromDate: this.fromDate, toDate: this.toDate }
      )
        .then((response) => {
          this.rows = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load sales report.'
          this.rows = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    exportReport () {
      this.errorMessage = ''
      this.isExporting = true
      axios({
        url: `${this.$apiAdress}/api/sales_report/download?token=${this.authToken()}`,
        method: 'POST',
        data: { fromDate: this.fromDate, toDate: this.toDate },
        responseType: 'blob'
      })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const anchor = document.createElement('a')
          anchor.href = blobUrl
          anchor.setAttribute('download', `Sales Report for ${this.fromDate} - ${this.toDate}.csv`)
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
        })
        .catch(async (error) => {
          console.log(error)
          const status = error && error.response && error.response.status
          let detail = ''
          const blob = error && error.response && error.response.data
          if (blob && typeof blob.text === 'function') {
            try {
              const text = await blob.text()
              try {
                const parsed = JSON.parse(text)
                detail = parsed.message || parsed.error || text
              } catch (e) {
                detail = text
              }
            } catch (e) {
              detail = ''
            }
          }
          if (detail && detail.length > 300) detail = detail.slice(0, 300) + '…'
          const prefix = status ? `Unable to export (HTTP ${status})` : 'Unable to export sales report.'
          this.errorMessage = detail ? `${prefix}: ${detail}` : prefix
        })
        .finally(() => {
          this.isExporting = false
        })
    }
  }
}
</script>

<style scoped>
.sales-report-page {
  padding: 0.25rem 0;
}

.sr-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.sr-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.sr-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.sr-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.sr-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.sr-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sr-export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.sr-export-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.sr-export-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sr-export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sr-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.sr-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.sr-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.sr-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.sr-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.sr-section__body {
  padding: 1rem;
}

.sr-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.sr-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.sr-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sr-field--range {
  flex: 0 1 460px;
  max-width: 480px;
}

.sr-field--presets {
  flex: 1 1 320px;
  min-width: 0;
}

.sr-field--action {
  flex: 0 0 auto;
  min-width: 120px;
}

.sr-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 36px;
  align-items: center;
}

.sr-preset {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.sr-preset:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.sr-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sr-preset.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.sr-search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.sr-search-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.sr-search-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.sr-search-btn:disabled,
.sr-search-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}

.sr-totals {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.sr-totals__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4f5560;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.sr-totals__chip strong {
  color: #1f2a44;
  font-variant-numeric: tabular-nums;
}

.sr-rate {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sr-rate--low { background: #dff5ea; color: #0e5b46; }
.sr-rate--mid { background: #fef3c7; color: #92400e; }
.sr-rate--high { background: #ffe4e6; color: #9f1239; }
.sr-rate--muted { background: #eef1f7; color: #4b5563; }
</style>
